import { apiBase } from './apiBase';
import { isTestModeEnabled } from './testMode';
import { mockApiFetch } from './mockRouter';
import { useInstanceStore } from '../store/useInstanceStore';

// Error thrown for non-2xx API responses. Carries the standard envelope's
// error code and HTTP status so callers can branch on them.
export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// Silent session recovery (CLE-439). When a protected request comes back 401
// (usually a short-lived access token that expired mid-session), we transparently
// refresh the token once and replay the request instead of surfacing the error to
// the UI — which previously crashed the page and forced a manual browser refresh.
//
// The refresh callback is registered by the auth store at startup. We use a
// registration hook rather than importing the store here to avoid a circular
// import (store -> authApi -> api). It returns the new access token, or null if
// the session could not be refreshed (in which case the 401 is propagated).
type RefreshFn = () => Promise<string | null>;
let refreshSessionFn: RefreshFn | null = null;

export function registerSessionRefresh(fn: RefreshFn): void {
  refreshSessionFn = fn;
}

// Collapses concurrent 401s into a single refresh call: if several requests fail
// at once, they all await the same in-flight refresh instead of each firing their
// own, which would race and repeatedly invalidate the freshly issued tokens.
let inFlightRefresh: Promise<string | null> | null = null;

export function refreshOnce(): Promise<string | null> {
  if (!refreshSessionFn) return Promise.resolve(null);
  if (!inFlightRefresh) {
    inFlightRefresh = refreshSessionFn().finally(() => {
      inFlightRefresh = null;
    });
  }
  return inFlightRefresh;
}

// Replace the Bearer token in a request's headers with the freshly refreshed one
// so the retry carries valid credentials. Headers may arrive as a Headers
// instance, an array of tuples, or a plain record.
function withFreshAuth(init: RequestInit | undefined, token: string): RequestInit {
  const headers = new Headers(init?.headers ?? {});
  headers.set('Authorization', `Bearer ${token}`);
  return { ...init, headers };
}

// Read the `exp` claim (seconds since epoch) from a JWT access token without a
// library. Used by the auth store to refresh the token proactively — shortly
// before it expires — so requests never hit an expired token in the first place
// (CLE-442). Returns null for anything that is not a well-formed JWT (e.g. the
// synthetic demo-mode token), so callers can skip scheduling for those.
export function getTokenExpiryMs(token: string | null | undefined): number | null {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    // JWT uses base64url; convert to the base64 `atob` understands and pad it.
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const payload = JSON.parse(atob(padded)) as { exp?: number };
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

function extractErrorMessage(body: unknown, fallback: string): { message: string; code?: string } {
  if (body && typeof body === 'object' && 'error' in body) {
    const err = (body as { error: unknown }).error;
    if (err && typeof err === 'object') {
      const e = err as { message?: string; code?: string };
      return { message: e.message ?? fallback, code: e.code };
    }
    if (typeof err === 'string') {
      return { message: err };
    }
  }
  return { message: fallback };
}

export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  // Auth endpoints ALWAYS hit the real backend, even in Test Mode (CLE-251).
  // Test Mode is a frontend-only data overlay and must never intercept the
  // authentication mechanism — otherwise enabling it would break real login
  // (the original CLE-251 bug). The dedicated demo account is handled entirely
  // in the auth store without any apiFetch call, so it never reaches here.
  const isAuthEndpoint = path.startsWith('/api/auth');

  // Test Mode (CLE-250): when enabled, serve a coherent simulated dataset from
  // the in-memory mock router instead of hitting the real backend. Writes are
  // no-op'd there, so real data is never touched.
  if (isTestModeEnabled() && !isAuthEndpoint) {
    return mockApiFetch<T>(path, init);
  }

  try {
    return await performFetch<T>(path, init);
  } catch (err) {
    // On an expired access token, refresh once and replay the request. We skip
    // this for auth endpoints (the refresh call itself is one) to avoid an
    // infinite loop, and only retry when a refresh callback is registered.
    if (err instanceof ApiError && err.status === 401 && !isAuthEndpoint && refreshSessionFn) {
      const token = await refreshOnce();
      if (token) {
        return performFetch<T>(path, withFreshAuth(init, token));
      }
    }
    throw err;
  }
}

async function performFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${apiBase()}${path}`, {
    // Spread `init` first so its own `headers` does not clobber the merged
    // object below. Previously `...init` came last and replaced the headers
    // entirely, dropping `Content-Type: application/json` on requests that
    // passed custom headers (e.g. Authorization). Without that content type,
    // Express's JSON body parser skipped the body, so POSTs arrived with an
    // empty `req.body` and failed validation (CLE-67).
    ...init,
    headers: { 'Content-Type': 'application/json', ...normalizeHeaders(init?.headers) },
  });

  const text = await res.text();
  const parsed: unknown = text ? JSON.parse(text) : undefined;

  if (!res.ok) {
    const { message, code } = extractErrorMessage(
      parsed,
      res.statusText || `Request failed: ${res.status}`,
    );
    // The Firmio Console suspended this instance: swap the whole app for the
    // notice (App.tsx renders SuspendedPage while the flag is set).
    if (code === 'INSTANCE_SUSPENDED') useInstanceStore.getState().setSuspended(message);
    throw new ApiError(res.status, message, code);
  }

  return parsed as T;
}

// Normalize any HeadersInit form into a plain record so it can be spread into
// the merged headers object. Needed because the 401 retry rebuilds headers as a
// `Headers` instance (see withFreshAuth), which would otherwise spread to `{}`.
function normalizeHeaders(headers: HeadersInit | undefined): Record<string, string> {
  if (!headers) return {};
  if (headers instanceof Headers) return Object.fromEntries(headers.entries());
  if (Array.isArray(headers)) return Object.fromEntries(headers);
  return headers as Record<string, string>;
}
